import React, { memo, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory, useLocation } from 'react-router-dom';

import WallpaperOpt from '../WallpaperOpt/WallpaperOpt';
import { CONTEXT } from '../../config';
import { WP_DOWNLOAD_WAIT, WP_DOWNLOAD_ING, WP_DOWNLOAD_FINISHED } from '../../constants/wallpaper';
import { logging, logPostInfo } from '../../actions/log';

import { hasClass, removeClass, addClass, getQueryString } from '../../utils/utils';
import transparent from '../../static/transparent.svg';
import wpAuthorDefault from '../../static/wp_author_default.png';

import styles from './NS_WallpaperCard.module.less';

const selectDownloadImgs = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper.wpDowloadImgs,
);
const selectWpOpteration = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper.wpOpteration,
);
const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

/**
 * cardInfo: {
 *   classType      // 用于判断此图片展示那个class
 *   isPreview      // 是否展示放大按钮
 *   isDeleteUsed   // 是否展示删除按钮，曾经使用的壁纸
 *   isDeleteColl   // 是否展示删除按钮，点过赞的
 *   isWallpaper    // 是否展示设为壁纸按钮
 *   isCollect      // 是否展示点赞按钮
 *
 *   pageSource     // 页面来源来判断，由此card进入详情页面后请求那个对应的接口数据
 *   code pageTage classId tagId colorId authorId ... // 为详情页请求接口所需要的的参数
 * }
 */
export default memo(({ wallpaper, cardInfo, logInfo }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  // 正在任务执行中的壁纸
  const wpDowloadImgs = useSelector(selectDownloadImgs);
  const wpOpteration = useSelector(selectWpOpteration);
  const logInfoData = useSelector(selectLogInfo);

  const [dowloadWP, setDowloadWP] = useState(null);
  const [collectNum, setCollectNum] = useState(wallpaper.collectCount);

  useEffect(() => {
    // 初始化心
    if (wallpaper.collectCount >= 10000) {
      setCollectNum(`${Math.round((wallpaper.collectCount / 10000) * 10) / 10}万`);
    } else {
      setCollectNum(wallpaper.collectCount);
    }
  }, [wallpaper && wallpaper.collectCount]);

  /**
   * 操作结束处理
   */
  useEffect(() => {
    if (
      wpOpteration &&
      wpOpteration.wallpaperId === wallpaper.wallpaperId &&
      wpOpteration.type === 1 &&
      wpOpteration.status === 0
    ) {
      const collectCount = wallpaper.collectCount + 1;
      if (collectCount >= 10000) {
        setCollectNum(`${Math.round((collectCount / 10000) * 10) / 10}万`);
      } else {
        setCollectNum(collectCount);
      }
    }
  }, [wpOpteration]);

  useEffect(() => {
    const found =
      wpDowloadImgs && wpDowloadImgs.find(wp => wp.wallpaperId === wallpaper.wallpaperId);
    setDowloadWP(found);
  }, [wpDowloadImgs]);

  const handleImg = useCallback(
    wp => {
      dispatch(
        logPostInfo({
          source_p: logInfo.source,
          source_pp: logInfoData.source_p,
        }),
      );

      setTimeout(() => {
        history.push(
          `${CONTEXT}/wpdetail?wallpaperId=${wp.wallpaperId}&pageSource=${cardInfo.pageSource ||
            'recommend'}&pageTage=${cardInfo.pageTage}&authorId=${cardInfo.authorId}&code=${
            cardInfo.code
          }&classId=${cardInfo.classId || ''}&tagId=${cardInfo.tagId ||
            ''}&colorId=${cardInfo.colorId || ''}`,
        );
      }, 20);
    },
    [logInfoData, cardInfo],
  );

  const onMouseOver = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName(styles.wpimg_img)[0];
    addClass(el, styles.image_mouse_in);
  }, []);

  const onMouseLeave = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName(styles.wpimg_img)[0];
    addClass(el, styles.image_mouse_out);
  }, []);

  const onAnimationEnd = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName(styles.wpimg_img)[0];
    if (hasClass(el, styles.image_mouse_out)) {
      removeClass(el, styles.image_mouse_in);
      removeClass(el, styles.image_mouse_out);
    }
  }, []);

  const onError = function(e, flag) {
    if (e.currentTarget && e.currentTarget.src) {
      e.currentTarget.src = transparent;
    }
  };

  const handleAuthor = useCallback(author => {
    // 更新页面 PV
    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'C_Wallpaper_AuthorMark',
        label: '',
        param2: {
          source: `${logInfo.source}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          authorId: author.authorId,
          authorName: author.authorName,
        },
      }),
    );

    dispatch(
      logPostInfo({
        source_p: logInfo.source,
        source_pp: logInfoData.source_p,
      }),
    );

    setTimeout(() => {
      if (
        location.pathname !== `${CONTEXT}/wpauthordetail` &&
        getQueryString('authorId') !== author.authorId
      ) {
        history.push(`${CONTEXT}/wpauthordetail?authorId=${author.authorId}`);
      }
    }, 20);
  });

  return (
    <div className={styles.wpimg}>
      <div
        className={styles.wpimg_box}
        onMouseOver={e => onMouseOver(e, wallpaper)}
        onMouseLeave={e => onMouseLeave(e, wallpaper)}
        onAnimationEnd={e => onAnimationEnd(e)}
      >
        <img
          className={`${styles.wpimg_img}`}
          src={wallpaper.wallpaperImg[0].uri || transparent}
          onClick={() => handleImg(wallpaper)}
          onError={onError}
          alt="img"
        />
        <div className={styles.wpimg_info} onClick={() => handleImg(wallpaper)}>
          <div className={styles.wpimg_info_like}>{collectNum}次收藏</div>
          <div className={styles.wpimg_opt}>
            <WallpaperOpt wallpaper={wallpaper} cardInfo={cardInfo} logInfo={logInfo} />
          </div>
        </div>

        {!cardInfo.authorId && wallpaper.author && wallpaper.author.authorName && (
          <div className={styles.wpimg_author} onClick={() => handleImg(wallpaper)}>
            <img
              className={styles.wpimg_author_header}
              src={wallpaper.author.headPortrait || wpAuthorDefault}
              onClick={e => {
                e.stopPropagation();
                handleAuthor(wallpaper.author);
              }}
              alt=""
            />
            <div
              className={styles.wpimg_author_name}
              title={wallpaper.author.authorName}
              onClick={e => {
                e.stopPropagation();
                handleAuthor(wallpaper.author);
              }}
            >
              {wallpaper.author.authorName}
            </div>
          </div>
        )}
      </div>
      {dowloadWP &&
        (dowloadWP.status === WP_DOWNLOAD_WAIT ||
          dowloadWP.status === WP_DOWNLOAD_ING ||
          dowloadWP.status === WP_DOWNLOAD_FINISHED) && (
          <div className={styles.wpimg_busying}>
            <div className={styles.wpimg_busying_progress}>
              <div className={styles.inner} style={{ width: `${dowloadWP.progress}%` }}></div>
            </div>
          </div>
        )}
    </div>
  );
});
